import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Base_Url } from '../config/domainConfig';
import './HeroSection.css';

const HeroSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    country: '',
    address: '',
    occupation: '',
    category: '',
    idea: ''
  });

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Dropdown data states
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Loading states
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // Category custom input state
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');

  // Dynamic Success Stories Data - This will be managed by admin later
  const [successStories] = useState([
    {
      id: 1,
      title: "From Startup to Success",
      description: "A young entrepreneur received funding and grew their business from 0 to 1M+ revenue in just 2 years.",
      author: "Rahul Sharma",
      company: "TechStart Solutions",
      amount: "₹50,00,000",
      image: null // Will be added by admin
    },
    {
      id: 2,
      title: "Empowering Rural Women",
      description: "A women's cooperative received funding to start a handicraft business, creating jobs for 50+ women.",
      author: "Priya Patel",
      company: "Rural Craft Collective",
      amount: "₹25,00,000",
      image: null
    },
    {
      id: 3,
      title: "Education for All",
      description: "An educational startup received funding to provide quality education to underprivileged children.",
      author: "Amit Kumar",
      company: "EduForAll Foundation",
      amount: "₹75,00,000",
      image: null
    }
  ]);

  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);

  // API Base URL
  const API_BASE_URL = `${Base_Url}/api`;

  // Fetch countries from API
  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await fetch(`${API_BASE_URL}/country-master/active/list`);
      const data = await response.json();
      if (data.success) {
        setCountries(data.data);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setLoadingCountries(false);
    }
  };

  // Fetch states by country
  const fetchStates = async (countryId) => {
    if (!countryId) {
      setStates([]);
      return;
    }
    
    setLoadingStates(true);
    try {
      const response = await fetch(`${API_BASE_URL}/state-master/country/${countryId}`);
      const data = await response.json();
      if (data.success) {
        setStates(data.data);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
    } finally {
      setLoadingStates(false);
    }
  };

  // Fetch categories from API
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await fetch(`${API_BASE_URL}/business-category-master/active/list`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Add custom category
  const addCustomCategory = async () => {
    if (!customCategory.trim()) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/business-category-master`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          categoryname: customCategory.trim(),
          categorycode: customCategory.trim().toUpperCase().replace(/\s+/g, '_'),
          description: `Custom category added by user: ${customCategory}`,
          status: 'active'
        }),
      });
      
      const data = await response.json();
      if (data.success) {
        // Add the new category to the list
        setCategories(prev => [...prev, data.data]);
        // Set the form data to the new category
        setFormData(prev => ({
          ...prev,
          category: data.data.categoryid
        }));
        // Reset custom category input
        setCustomCategory('');
        setShowCustomCategory(false);
      }
    } catch (error) {
      console.error('Error adding custom category:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchCountries();
    fetchCategories();
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    if (formData.country) {
      fetchStates(formData.country);
      // Reset state when country changes
      setFormData(prev => ({
        ...prev,
        state: ''
      }));
    } else {
      setStates([]);
    }
  }, [formData.country]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (type === 'video') {
      setSelectedVideo(file);
    } else if (type === 'pdf') {
      setSelectedPdf(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Show loading state
    setSubmitting(true);
    
    try {
      // Create FormData for file uploads
      const formDataToSend = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      
      // Add files if selected
      if (selectedVideo) {
        formDataToSend.append('video_file', selectedVideo);
      }
      if (selectedPdf) {
        formDataToSend.append('pdf_file', selectedPdf);
      }
      
      // Submit application
      const response = await fetch(`${API_BASE_URL}/applications/submit`, {
        method: 'POST',
        body: formDataToSend
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to submit application');
      }
      
      // Application submitted successfully, now create payment order
      const userToken = localStorage.getItem('userToken');
      const paymentResponse = await fetch(`${API_BASE_URL}/payments/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(userToken ? { 'Authorization': `Bearer ${userToken}` } : {})
        },
        body: JSON.stringify({
          applicationId: data.data.applicationId
        }),
      });
      
      const paymentData = await paymentResponse.json();
      
      if (!paymentData.success) {
        throw new Error(paymentData.message || 'Failed to create payment order');
      }
      
             // Initialize Razorpay payment
       const options = {
         key: 'rzp_test_BWMg0dPw8Az8vc', // Your Razorpay key
        amount: paymentData.data.amount,
        currency: paymentData.data.currency,
        name: 'Vikas Fund',
        description: 'Application Submission Fee',
        order_id: paymentData.data.orderId,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await fetch(`${API_BASE_URL}/payments/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(userToken ? { 'Authorization': `Bearer ${userToken}` } : {})
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                applicationId: data.data.applicationId
              }),
            });
            
            const verifyData = await verifyResponse.json();
            
            if (verifyData.success) {
              // Payment successful and user account created
              alert('Payment successful! Your login credentials have been sent to your email.');
              // Reset form
              setFormData({
                name: '', email: '', phone: '', city: '', state: '', country: '',
                address: '', occupation: '', category: '', idea: ''
              });
              setSelectedVideo(null);
              setSelectedPdf(null);
            } else {
              throw new Error(verifyData.message || 'Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            alert('Payment verification failed: ' + error.message);
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#667eea'
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
      
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Error submitting application: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDotClick = (index) => {
    setCurrentStoryIndex(index);
  };

  const currentStory = successStories[currentStoryIndex];

  return (
    <section id="home" className="hero-section">
      <div className="hero-container">
        {/* Left Column - Success Stories */}
        <div className="hero-left">
          <div className="success-stories">
            <h1 className="success-title">{t('stories.title')}</h1>
            
            {/* Dynamic Success Story Content */}
            <div className="story-content">
              <h3 className="story-title">{currentStory.title}</h3>
              <p className="story-description">{currentStory.description}</p>
              <div className="story-details">
                <div className="story-author">
                  <strong>{currentStory.author}</strong>
                  <span>{currentStory.company}</span>
                </div>
                <div className="story-amount">
                  <span className="amount-label">{t('periods.funding_received')}</span>
                  <span className="amount-value">{currentStory.amount}</span>
                </div>
              </div>
            </div>

            {/* Pagination Dots */}
            <div className="pagination-dots">
              {successStories.map((_, index) => (
                <span
                  key={index}
                  className={`dot ${index === currentStoryIndex ? 'active' : ''}`}
                  onClick={() => handleDotClick(index)}
                ></span>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Registration Form */}
        <div className="hero-right">
          <div className="registration-form">
            <h2 className="form-title">{t('form.submit')}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                {/* Left Column of Form */}
                <div className="form-column">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">{t('form.full_name')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">{t('form.email')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Your Email Address"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">{t('form.phone')}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="Your Phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="country" className="form-label">Country</label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      disabled={loadingCountries}
                    >
                      <option value="">Select Country</option>
                      {loadingCountries ? (
                        <option value="" disabled>Loading countries...</option>
                      ) : (
                        countries.map(country => (
                          <option key={country.countryid} value={country.countryid}>
                            {country.countryname}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="state" className="form-label">State</label>
                    <select
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      disabled={loadingStates}
                    >
                      <option value="">Select State</option>
                      {loadingStates ? (
                        <option value="" disabled>Loading states...</option>
                      ) : (
                        states.map(state => (
                          <option key={state.stateid} value={state.stateid}>
                            {state.statename}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="city" className="form-label">City</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      placeholder="Your City"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                {/* Right Column of Form */}
                <div className="form-column">
                  <div className="form-group">
                    <label htmlFor="address" className="form-label">Address</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      placeholder="Your Address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="occupation" className="form-label">Occupation</label>
                    <input
                      type="text"
                      id="occupation"
                      name="occupation"
                      placeholder="Your Occupation / Designation"
                      value={formData.occupation}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="category" className="form-label">{t('form.business_category')}</label>
                    {!showCustomCategory ? (
                      <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={(e) => {
                          if (e.target.value === 'other') {
                            setShowCustomCategory(true);
                            setFormData(prev => ({ ...prev, category: '' }));
                          } else {
                            setFormData(prev => ({ ...prev, category: e.target.value }));
                          }
                        }}
                        required
                        disabled={loadingCategories}
                      >
                        <option value="">Select Category</option>
                        {loadingCategories ? (
                          <option value="" disabled>Loading categories...</option>
                        ) : (
                          <>
                            {categories.map(category => (
                              <option key={category.categoryid} value={category.categoryid}>
                                {category.categoryname}
                              </option>
                            ))}
                            <option value="other">Other</option>
                          </>
                        )}
                      </select>
                    ) : (
                      <div className="custom-category-container">
                        <input
                          type="text"
                          id="custom-category"
                          placeholder="Enter your category"
                          value={customCategory}
                          onChange={(e) => setCustomCategory(e.target.value)}
                        />
                        <div className="custom-category-buttons">
                          <button
                            type="button"
                            className="save-category-btn"
                            onClick={addCustomCategory}
                            disabled={!customCategory.trim()}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="cancel-category-btn"
                            onClick={() => {
                              setShowCustomCategory(false);
                              setCustomCategory('');
                              setFormData(prev => ({ ...prev, category: '' }));
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="idea" className="form-label">Business Idea</label>
                    <textarea
                      id="idea"
                      name="idea"
                      placeholder="Describe your idea..."
                      value={formData.idea}
                      onChange={handleInputChange}
                      rows="4"
                      required
                    ></textarea>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="category" className="form-label">{t('form.business_category')}</label>
                    {!showCustomCategory ? (
                      <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={(e) => {
                          if (e.target.value === 'other') {
                            setShowCustomCategory(true);
                            setFormData(prev => ({ ...prev, category: '' }));
                          } else {
                            setFormData(prev => ({ ...prev, category: e.target.value }));
                          }
                        }}
                        required
                        disabled={loadingCategories}
                      >
                        <option value="">Select Category</option>
                        {loadingCategories ? (
                          <option value="" disabled>Loading categories...</option>
                        ) : (
                          <>
                            {categories.map(category => (
                              <option key={category.categoryid} value={category.categoryid}>
                                {category.categoryname}
                              </option>
                            ))}
                            <option value="other">Other</option>
                          </>
                        )}
                      </select>
                    ) : (
                      <div className="custom-category-container">
                        <input
                          type="text"
                          id="custom-category"
                          placeholder="Enter your category"
                          value={customCategory}
                          onChange={(e) => setCustomCategory(e.target.value)}
                        />
                        <div className="custom-category-buttons">
                          <button
                            type="button"
                            className="save-category-btn"
                            onClick={addCustomCategory}
                            disabled={!customCategory.trim()}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="cancel-category-btn"
                            onClick={() => {
                              setShowCustomCategory(false);
                              setCustomCategory('');
                              setFormData(prev => ({ ...prev, category: '' }));
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* File Upload Section */}
              <div className="file-upload-section">
                <div className="file-upload-group">
                  <label htmlFor="video-upload" className="form-label">Upload Video File</label>
                  <input
                    id="video-upload"
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleFileChange(e, 'video')}
                  />
                </div>
                
                <div className="file-upload-group">
                  <label htmlFor="pdf-upload" className="form-label">Upload Your PDF Document</label>
                  <input
                    id="pdf-upload"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileChange(e, 'pdf')}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button type="submit" className="submit-btn" disabled={submitting}>
                {submitting ? t('common.loading') : t('form.submit')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection; 